import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { FaCheckCircle, FaEnvelope, FaShoppingBag, FaFilePdf, FaFileExcel } from 'react-icons/fa';
import { MdPayment, MdLocalShipping } from 'react-icons/md';
import SERVER from '../hooks/useServer';
import { useCart } from '../hooks/cart';
import { useTranslation } from 'react-i18next';

const OrderSuccess = () => {
    const { t } = useTranslation();
    const { clearCart } = useCart();
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get('session_id');
    const [orderNumber, setOrderNumber] = useState('');
    const [order, setOrder] = useState(null);

    useEffect(() => {
        const verifyOrder = async () => {
            try {
                // Check if we have a session ID (Stripe payment)
                if (sessionId) {
                    const response = await axios.get(`${SERVER}/stripe/verify-payment?session_id=${sessionId}`);
                    setOrderNumber(response.data.orderNumber);
                    clearCart();
                } 
                // Check if we have an order number in state (Cash on Delivery)
                else if (location.state?.orderNumber) {
                    const response = await axios.get(`${SERVER}/stripe/order/${location.state.orderNumber}`);
                    setOrder(response.data);
                    setOrderNumber(response.data.order_number);
                    clearCart();
                } 
                // No valid order found
                else {
                    navigate('/');
                }
            } catch (error) {
                toast.error(t('orderSuccess.errorRetrieving'));
                console.error('Order verification error:', error);
                navigate('/');
            }
        };

        verifyOrder();
    }, [sessionId, navigate, clearCart, location.state, t]);

    const downloadPdf = async () => {
        if (!orderNumber) {
            toast.error(t('orderSuccess.orderNotFound'));
            return;
        }

        try {
            const response = await axios.get(`${SERVER}/commandes/${orderNumber}/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `commande_${orderNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error(t('orderSuccess.errorDownloadPdf'));
            console.error('PDF download error:', error);
        }
    };

    const downloadExcel = async () => {
        if (!orderNumber) {
            toast.error(t('orderSuccess.orderNotFound'));
            return;
        }

        try {
            const response = await axios.get(`${SERVER}/commandes/${orderNumber}/excel`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `commande_${orderNumber}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error(t('orderSuccess.errorDownloadExcel'));
            console.error('Excel download error:', error);
        }
    };

    const renderPaymentMessage = () => {
        if (!order) return null;
        
        if (order.payment_method === 'stripe') {
            return (
                <div className="flex items-center justify-center gap-2 text-lg text-gray-700 mb-4">
                    <MdPayment className="text-primary" size={24} />
                    <span>{t('orderSuccess.paymentReceived')}</span>
                </div>
            );
        } else {
            return (
                <div className="flex items-center justify-center gap-2 text-lg text-gray-700 mb-4">
                    <MdPayment className="text-primary" size={24} />
                    <span>{t('orderSuccess.cashOnDelivery')}</span>
                </div>
            );
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen py-12 px-4 pt-24">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 max-w-2xl w-full text-center">
                {/* Success Icon */}
                <div className="flex justify-center mb-6">
                    <div className="bg-green-100 rounded-full p-6">
                        <FaCheckCircle className="text-green-500 text-6xl" />
                    </div>
                </div>

                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                    {t('orderSuccess.title')}
                </h1>
                
                {/* Payment Message */}
                {renderPaymentMessage()}
                
                {/* Email Confirmation */}
                <div className="flex items-center justify-center gap-2 text-lg text-gray-700 mb-4">
                    <FaEnvelope className="text-primary" size={24} />
                    <span>{t('orderSuccess.emailConfirmation')}</span>
                </div>
                
                {/* Shipping Info */}
                <div className="flex items-center justify-center gap-2 text-lg text-gray-700 mb-8">
                    <MdLocalShipping className="text-primary" size={24} />
                    <span>{t('orderSuccess.shippingDetails')}</span>
                </div>

                {/* Order Details */}
                {orderNumber && (
                    <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-200">
                        <p className="text-gray-700 mb-2">
                            {t('orderSuccess.orderNumber')} <span className="font-bold text-primary text-xl">{orderNumber}</span>
                        </p>
                        <p className="text-sm text-gray-600 mb-6">
                            {t('orderSuccess.keepReference')}
                        </p>
                        
                        {/* Download Buttons */}
                        <div className="flex flex-wrap gap-4 justify-center">
                            <button 
                                onClick={downloadPdf} 
                                className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all transform hover:scale-105 shadow-md font-medium"
                            >
                                <FaFilePdf size={20} />
                                {t('orderSuccess.downloadPdf')}
                            </button>
                            <button 
                                onClick={downloadExcel} 
                                className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all transform hover:scale-105 shadow-md font-medium"
                            >
                                <FaFileExcel size={20} />
                                {t('orderSuccess.downloadExcel')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Back to Shop Button */}
                <button 
                    className="flex items-center justify-center gap-2 mx-auto bg-black text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium"
                    onClick={() => navigate('/')}
                >
                    <FaShoppingBag size={18} />
                    {t('orderSuccess.backToShop')}
                </button>
            </div>
        </div>
    );
};

export default OrderSuccess;